<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'
import axios from 'axios'
import { useRouter } from 'vue-router'

const router = useRouter()
const loading = ref(false)
const errorMessage = ref('')
const members = ref<any[]>([])
const filters = reactive({
  search: '',
  statut_kyc: '',
})
const pagination = reactive({
  pageNumber: 1,
  pageSize: 10,
  total: 0,
  last_page: 1,
})

async function fetchMembers() {
  loading.value = true
  errorMessage.value = ''
  try {
    const params: Record<string, any> = {
      pageNumber: pagination.pageNumber,
      pageSize: pagination.pageSize,
    }
    if (filters.search) params.search = filters.search
    if (filters.statut_kyc) params.statut_kyc = filters.statut_kyc
    const { data } = await axios.get('/kyc/members', { params })
    members.value = data.data || []
    pagination.total = data.total || 0
    pagination.last_page = data.last_page || 1
  } catch (e: any) {
    errorMessage.value = e.response?.data?.message || 'Erreur lors du chargement des membres'
  } finally {
    loading.value = false
  }
}

function applyFilters() {
  pagination.pageNumber = 1
  fetchMembers()
}

function goToDetail(member: any) {
  router.push({ name: 'collectivites-kyc-membre-detail', params: { id: member.id } })
}

function statutLabel(member: any): { label: string; cls: string } {
  const statut = member?.kyc?.statut
  if (!member?.kyc) return { label: 'Non rempli', cls: 'tag-state warning stroke' }
  if (statut === 'verified') return { label: 'Validé', cls: 'tag-state success stroke' }
  if (statut === 'rejected') return { label: 'Rejeté', cls: 'tag-state danger stroke' }
  return { label: 'En attente', cls: 'tag-state warning stroke' }
}

onMounted(fetchMembers)
</script>

<template>
  <section class="container-full-5 p-5">
    <h2 class="secondary-font gap-4 mt-2 mb-6">KYC des membres</h2>

    <div v-if="errorMessage" class="alert alert-danger mb-4">{{ errorMessage }}</div>

    <div class="s20 widget px-6 pt-6 pb-3 mb-5">
      <div class="container-full-2 s20 my-3 flex gap-3">
        <div class="s7">
          <input
            type="text"
            class="form-input"
            placeholder="Rechercher (nom, email)"
            v-model="filters.search"
            @keyup.enter="applyFilters"
          />
        </div>
        <div class="s5">
          <select class="form-input-select" v-model="filters.statut_kyc" @change="applyFilters">
            <option value="">Tous statuts KYC</option>
            <option value="pending">En attente</option>
            <option value="verified">Validé</option>
            <option value="rejected">Rejeté</option>
            <option value="no_kyc">Pas de KYC</option>
          </select>
        </div>
        <button class="btn normal" @click="applyFilters">Filtrer</button>
      </div>
    </div>

    <div v-if="loading" class="flex-center py-10">
      <div class="loader dark"></div>
    </div>

    <div v-else class="s20 widget pb-2">
      <table class="table separator skin3 rounded">
        <thead>
          <tr>
            <th>Membre</th>
            <th>Email</th>
            <th>Téléphone</th>
            <th>Statut KYC</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="members.length === 0">
            <td colspan="5" class="text-center text-grey-500 py-5">Aucun membre trouvé</td>
          </tr>
          <tr v-for="m in members" :key="m.id" class="over-effect">
            <td>{{ m.nom }} {{ m.prenom }}</td>
            <td>{{ m.email }}</td>
            <td>{{ m.telephone }}</td>
            <td>
              <span :class="statutLabel(m).cls">{{ statutLabel(m).label }}</span>
            </td>
            <td class="flex-right">
              <button class="btn white2 active" @click="goToDetail(m)">
                Gérer
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="pagination.total > 0" class="text-center mt-4 mb-4">
        <button
          class="btn"
          :disabled="pagination.pageNumber <= 1"
          @click="(pagination.pageNumber--, fetchMembers())"
        >
          Précédent
        </button>
        <span class="mx-3">Page {{ pagination.pageNumber }} / {{ pagination.last_page }}</span>
        <button
          class="btn"
          :disabled="pagination.pageNumber >= pagination.last_page"
          @click="(pagination.pageNumber++, fetchMembers())"
        >
          Suivant
        </button>
      </div>
    </div>
  </section>
</template>
